import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  InputAdornment,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';
import ClearIcon from '@mui/icons-material/Clear';
import TablePagination from '@mui/material/TablePagination';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface Bank {
  id: string;
  name: string;
}

interface BankAccount {
  id: string;
  bankId: string;
  accountNumber: string;
  accountType: 'Current' | 'Savings' | 'Term Deposit' | 'Other';
  currency: string;
  accountHolderName: string;
  branch: string;
  openingDate: string;
  status: 'Active' | 'Inactive';
  creationDate: string;
  lastUpdated: string;
  createdBy: string;
  lastUpdatedBy: string;
}

// Mock banks data - this should come from your bank management system
const mockBanks: Bank[] = [
  { id: 'B001', name: 'Saudi National Bank' },
  { id: 'B002', name: 'Al Rajhi Bank' },
  { id: 'B003', name: 'Riyad Bank' },
];

const currencies = ['SAR', 'USD', 'EUR', 'GBP'];

const accountTypes = ['Current', 'Savings', 'Term Deposit', 'Other'];

const initialBankAccounts: BankAccount[] = [
  {
    id: 'BA001',
    bankId: 'B001',
    accountNumber: '1234567890',
    accountType: 'Current',
    currency: 'SAR',
    accountHolderName: 'MJS Holding Company',
    branch: 'Riyadh Main Branch',
    openingDate: '2024-01-01',
    status: 'Active',
    creationDate: '2024-01-01',
    lastUpdated: '2024-03-15',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  },
  {
    id: 'BA002',
    bankId: 'B002',
    accountNumber: '9876543210',
    accountType: 'Savings',
    currency: 'USD',
    accountHolderName: 'MJS Subsidiary LLC',
    branch: 'Jeddah Branch',
    openingDate: '2023-11-10',
    status: 'Active',
    creationDate: '2023-11-10',
    lastUpdated: '2024-02-20',
    createdBy: 'Finance Manager',
    lastUpdatedBy: 'Finance Manager'
  },
  {
    id: 'BA003',
    bankId: 'B003',
    accountNumber: '1122334455',
    accountType: 'Term Deposit',
    currency: 'EUR',
    accountHolderName: 'MJS Investment',
    branch: 'Dammam Branch',
    openingDate: '2022-06-15',
    status: 'Inactive',
    creationDate: '2022-06-15',
    lastUpdated: '2023-12-01',
    createdBy: 'Treasury',
    lastUpdatedBy: 'Treasury'
  },
  {
    id: 'BA004',
    bankId: 'B001',
    accountNumber: '5566778899',
    accountType: 'Current',
    currency: 'GBP',
    accountHolderName: 'MJS Trading',
    branch: 'Riyadh North Branch',
    openingDate: '2024-02-01',
    status: 'Active',
    creationDate: '2024-02-01',
    lastUpdated: '2024-03-10',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  },
  {
    id: 'BA005',
    bankId: 'B002',
    accountNumber: '6677889900',
    accountType: 'Other',
    currency: 'SAR',
    accountHolderName: 'MJS Logistics',
    branch: 'Jeddah Industrial Branch',
    openingDate: '2023-09-20',
    status: 'Inactive',
    creationDate: '2023-09-20',
    lastUpdated: '2024-01-05',
    createdBy: 'Finance Manager',
    lastUpdatedBy: 'Finance Manager'
  },
  {
    id: 'BA006',
    bankId: 'B003',
    accountNumber: '3344556677',
    accountType: 'Savings',
    currency: 'USD',
    accountHolderName: 'MJS Real Estate',
    branch: 'Dammam Central Branch',
    openingDate: '2022-12-12',
    status: 'Active',
    creationDate: '2022-12-12',
    lastUpdated: '2024-03-01',
    createdBy: 'Treasury',
    lastUpdatedBy: 'Treasury'
  }
];

const BankAccountManagement: React.FC = () => {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>(initialBankAccounts);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
  const [viewingAccount, setViewingAccount] = useState<BankAccount | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBank, setFilterBank] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState<Partial<BankAccount>>({
    bankId: '',
    accountNumber: '',
    accountType: 'Current',
    currency: 'SAR',
    accountHolderName: '',
    branch: '',
    openingDate: format(new Date(), 'yyyy-MM-dd'),
    status: 'Active'
  });

  const handleOpenDialog = (account?: BankAccount) => {
    if (account) {
      setEditingAccount(account);
      setFormData(account);
    } else {
      setEditingAccount(null);
      setFormData({
        bankId: '',
        accountNumber: '',
        accountType: 'Current',
        currency: 'SAR',
        accountHolderName: '',
        branch: '',
        openingDate: format(new Date(), 'yyyy-MM-dd'),
        status: 'Active'
      });
    }
    setOpenDialog(true);
  };

  const handleViewDialog = (account: BankAccount) => {
    setViewingAccount(account);
    setViewDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setViewDialog(false);
    setEditingAccount(null);
    setViewingAccount(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    // Validate required fields
    const requiredFields: (keyof BankAccount)[] = ['bankId', 'accountNumber', 'accountHolderName'];
    const missingFields = requiredFields.filter(field => !formData[field as keyof Partial<BankAccount>]);
    
    if (missingFields.length > 0) {
      alert(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Validate max lengths
    if (formData.accountNumber && formData.accountNumber.length > 30) {
      alert('Account Number cannot exceed 30 characters');
      return;
    }
    if (formData.accountHolderName && formData.accountHolderName.length > 100) {
      alert('Account Holder Name cannot exceed 100 characters');
      return;
    }
    if (formData.branch && formData.branch.length > 100) {
      alert('Branch cannot exceed 100 characters');
      return;
    }

    if (editingAccount) {
      // Update existing account
      setBankAccounts(prev => prev.map(account => 
        account.id === editingAccount.id 
          ? { 
              ...account, 
              ...formData, 
              lastUpdated: format(new Date(), 'yyyy-MM-dd'),
              lastUpdatedBy: 'Current User'
            }
          : account
      ));
    } else {
      // Add new account
      const newAccount: BankAccount = {
        ...formData as BankAccount,
        id: `BA${String(bankAccounts.length + 1).padStart(3, '0')}`,
        creationDate: format(new Date(), 'yyyy-MM-dd'),
        lastUpdated: format(new Date(), 'yyyy-MM-dd'),
        createdBy: 'Current User',
        lastUpdatedBy: 'Current User'
      };
      setBankAccounts(prev => [...prev, newAccount]);
    }
    handleCloseDialog();
  };

  const handleDelete = (accountId: string) => {
    // Add your validation check here
    if (window.confirm('Are you sure you want to delete this bank account? This action cannot be undone.')) {
      setBankAccounts(prev => prev.filter(account => account.id !== accountId));
    }
  };

  const filteredAccounts = bankAccounts.filter(account => {
    const matchesSearch = 
      account.accountNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.accountHolderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mockBanks.find(bank => bank.id === account.bankId)?.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesBank = filterBank ? account.bankId === filterBank : true;
    const matchesType = filterType ? account.accountType === filterType : true;

    return matchesSearch && matchesBank && matchesType;
  });

  const getBankName = (bankId: string) => {
    return mockBanks.find(bank => bank.id === bankId)?.name || '';
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedAccounts = filteredAccounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/parameters">
          Parameters
        </MuiLink>
        <Typography color="text.primary">Bank Account Management</Typography>
      </Breadcrumbs>
      <Typography variant="h4" gutterBottom>
        Bank Account Management
      </Typography>

      {/* Search and Filter */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<TableChartIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            bgcolor: '#fff',
            color: '#1E293B',
            borderColor: '#E5E7EB',
            textTransform: 'none',
            boxShadow: 'none',
            px: 2.5,
            '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
          }}
        >
          Export to Excel
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            bgcolor: '#fff',
            color: '#1E293B',
            borderColor: '#E5E7EB',
            textTransform: 'none',
            boxShadow: 'none',
            px: 2.5,
            '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
          }}
        >
          Print
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          color="primary"
          sx={{
            fontWeight: 600,
            px: 4,
            borderRadius: 2,
            boxShadow: '0 1px 2px 0 rgba(59,130,246,0.08)',
            textTransform: 'none',
            '&:hover': { bgcolor: '#2563EB' }
          }}
        >
          Add New Account
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: 3,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.03)',
        }}
      >
        <TextField
          placeholder="Search accounts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280' }} />
              </InputAdornment>
            ),
            sx: { bgcolor: '#fff', borderRadius: 2, height: 40 }
          }}
          sx={{ minWidth: 240, maxWidth: 320 }}
        />
        <FormControl sx={{ minWidth: 240, maxWidth: 320, bgcolor: '#fff', borderRadius: 2 }} size="small">
          <InputLabel>Filter by Bank</InputLabel>
          <Select
            value={filterBank}
            label="Filter by Bank"
            onChange={(e) => setFilterBank(e.target.value)}
            sx={{ height: 40 }}
          >
            <MenuItem value="">All Banks</MenuItem>
            {mockBanks.map((bank) => (
              <MenuItem key={bank.id} value={bank.id}>{bank.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 240, maxWidth: 320, bgcolor: '#fff', borderRadius: 2 }} size="small">
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={filterType}
            label="Filter by Type"
            onChange={(e) => setFilterType(e.target.value)}
            sx={{ height: 40 }}
          >
            <MenuItem value="">All Types</MenuItem>
            {accountTypes.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          size="medium"
          sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<ClearIcon />}
          onClick={() => { setSearchTerm(''); setFilterBank(''); setFilterType(''); }}
          size="medium"
          sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
        >
          Reset
        </Button>
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)', background: '#fff', mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Account ID</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Bank</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Account Number</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Account Holder</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAccounts.map((account, idx) => (
              <TableRow
                key={account.id}
                hover
                sx={{
                  '&:hover': { bgcolor: '#F5F6FF' },
                  borderBottom: idx !== paginatedAccounts.length - 1 ? '1px solid #E5E7EB' : 'none',
                  transition: 'background 0.2s',
                }}
              >
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{account.id}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{getBankName(account.bankId)}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{account.accountNumber}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{account.accountHolderName}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{account.accountType}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Chip 
                    label={account.status} 
                    size="small"
                    sx={{ 
                      bgcolor: account.status === 'Active' ? '#D1FAE5' : '#FEE2E2',
                      color: account.status === 'Active' ? '#059669' : '#DC2626',
                      fontWeight: 500
                    }} 
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Stack direction="row" spacing={1}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewDialog(account)}
                      sx={{ color: '#6366F1' }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(account)}
                      sx={{ color: '#6366F1' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(account.id)}
                      sx={{ color: '#DC2626' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredAccounts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      {/* Add/Edit Bank Account Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          {editingAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Account Information</Typography>
              <Grid container spacing={2}>
                {editingAccount && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account ID"
                      value={editingAccount.id}
                      disabled
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Bank</InputLabel>
                    <Select
                      value={formData.bankId}
                      label="Bank"
                      onChange={(e) => handleInputChange('bankId', e.target.value)}
                    >
                      {mockBanks.map((bank) => (
                        <MenuItem key={bank.id} value={bank.id}>{bank.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    value={formData.accountNumber}
                    onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                    required
                    inputProps={{ maxLength: 30 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Holder Name"
                    value={formData.accountHolderName}
                    onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                    required
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Account Type</InputLabel>
                    <Select
                      value={formData.accountType}
                      label="Account Type"
                      onChange={(e) => handleInputChange('accountType', e.target.value)}
                    >
                      {accountTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={formData.currency}
                      label="Currency"
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                    >
                      {currencies.map((currency) => (
                        <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Branch"
                    value={formData.branch}
                    onChange={(e) => handleInputChange('branch', e.target.value)}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Opening Date"
                    value={formData.openingDate}
                    onChange={(e) => handleInputChange('openingDate', e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              color: '#374151',
              '&:hover': { bgcolor: '#F3F4F6' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              bgcolor: '#6366F1',
              '&:hover': { bgcolor: '#4F46E5' },
              textTransform: 'none',
              px: 3
            }}
          >
            {editingAccount ? 'Update Account' : 'Add Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Bank Account Dialog */}
      <Dialog 
        open={viewDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          View Bank Account Details
        </DialogTitle>
        <DialogContent>
          {viewingAccount && (
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Account Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account ID"
                      value={viewingAccount.id}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bank"
                      value={getBankName(viewingAccount.bankId)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      value={viewingAccount.accountNumber}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account Holder Name"
                      value={viewingAccount.accountHolderName}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account Type"
                      value={viewingAccount.accountType}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Currency"
                      value={viewingAccount.currency}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Branch"
                      value={viewingAccount.branch}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Opening Date"
                      value={viewingAccount.openingDate}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Status"
                      value={viewingAccount.status}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>System Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Creation Date"
                      value={viewingAccount.creationDate}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Updated Date"
                      value={viewingAccount.lastUpdated}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Created By"
                      value={viewingAccount.createdBy}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Updated By"
                      value={viewingAccount.lastUpdatedBy}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              color: '#374151',
              '&:hover': { bgcolor: '#F3F4F6' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankAccountManagement; 